import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

import useCategoryData from "../../hooks/useCategoryData";
import useFileUpload from "../../hooks/useFileUpload";
import usePieChart from "../../hooks/usePieChart";
import { subCategoriesMap } from "../../utils/subCategoryMap";

import SubCategoryCard from "./SubCategoryCard";
import PieChartCard from "./PieChartCard";
import SubCategoryModal from "./SubCategoryModal";
import TrustedContactModal from "./TrustedContactModal";
import InactivitySettingsModal from "./InactivitySettingsModal";

import styles from "./CategoryDetails.module.css";

export default function CategoryDetails() {
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const { data, setData, loading, saveData } = useCategoryData(categoryId);
  const { uploadFile } = useFileUpload();

  const subCategories = subCategoriesMap[categoryId] || [];
  const { values, pieData } = usePieChart(data, subCategories);

  const [activeSubCat, setActiveSubCat] = useState(null);

  const openModal = (subCat) => setActiveSubCat(subCat);
  const closeModal = () => setActiveSubCat(null);

  // ⭐ Auto‑initialize missing subcategories
  useEffect(() => {
    if (!data) return;

    const updated = { ...data };
    let changed = false;

    subCategories.forEach((subCat) => {
      // Trusted Contacts → array
      if (subCat === "trustedContacts") {
        if (!updated[subCat]) {
          updated[subCat] = [{ contactEmail: "", phoneNumber: "" }];
          changed = true;
        }
        return;
      }

      // Inactivity Settings → object
      if (subCat === "inactivitySettings") {
        if (!updated[subCat] || typeof updated[subCat] !== "object") {
          updated[subCat] = {
            inactivityPeriodInMonths: 0,
            reminderCountBeforeSend: 0,
          };
          changed = true;
        }
        return;
      }

      // Default → array
      if (!updated[subCat]) {
        updated[subCat] = [];
        changed = true;
      }
    });

    if (changed) setData(updated);
  }, [data, subCategories, setData]);

  // ADD ITEM
  const handleAdd = (subCat) => {
    setData((prev) => ({
      ...prev,
      [subCat]: [
        ...(prev[subCat] || []),
        { name: "", amount: 0, note: "", fileURL: "", fileName: "" },
      ],
    }));
  };

  // REMOVE ITEM
  const handleRemove = (subCat, index) => {
    setData((prev) => ({
      ...prev,
      [subCat]: prev[subCat].filter((_, i) => i !== index),
    }));
  };

  // CHANGE ITEM
  const handleChange = (subCat, index, updatedItem) => {
    setData((prev) => ({
      ...prev,
      [subCat]: prev[subCat].map((item, i) =>
        i === index ? updatedItem : item
      ),
    }));
  };

  // FILE UPLOAD
  const handleFileUpload = async (subCat, index, file) => {
    const { url, fileName } = await uploadFile(file);

    setData((prev) => ({
      ...prev,
      [subCat]: prev[subCat].map((item, i) =>
        i === index ? { ...item, fileURL: url, fileName } : item
      ),
    }));
  };

  // SAVE
  const handleSave = async () => {
    await saveData(data);
    closeModal();
  };

  if (loading) return <p className={styles.loading}>Loading...</p>;

  return (
    <div className={styles.categoryWrapper}>
      {/* HEADER */}
      <div className={styles.headerTop}>
        <button className={styles.btnBack} onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h1 className={styles.title}>{categoryId}</h1>
      </div>

      {/* SUBCATEGORY GRID */}
      <div className={styles.subcategoryGrid}>
        {subCategories.map((subCat) => (
          <SubCategoryCard
            key={subCat}
            subCat={subCat}
            items={data[subCat]}
            openModal={openModal}
          />
        ))}
      </div>

      {/* ⭐ MODAL ROUTING */}

      {/* Trusted Contacts */}
      {activeSubCat === "trustedContacts" && (
	  <TrustedContactModal
		data={Array.isArray(data["trustedContacts"]) ? data["trustedContacts"] : []}
		onSave={(updated) => {
		  const newData = { ...data, trustedContacts: updated };
		  setData(newData);
		  saveData(newData);   // ⭐ REQUIRED
		  closeModal();
		}}
		onCancel={closeModal}
	  />
	)}


      {/* Inactivity Settings */}
		{activeSubCat === "inactivitySettings" && (
		  <InactivitySettingsModal
			data={
			  data["inactivitySettings"] &&
			  typeof data["inactivitySettings"] === "object"
				? data["inactivitySettings"]
				: {
					inactivityPeriodInMonths: 0,
					reminderCountBeforeSend: 0,
				  }
			}
			onSave={(updated) => {
			  const newData = { ...data, inactivitySettings: updated };
			  setData(newData);
			  saveData(newData);   // ⭐ REQUIRED
			  closeModal();
			}}
			onCancel={closeModal}
		  />
		)}


      {/* Default modal */}
      {activeSubCat &&
        activeSubCat !== "trustedContacts" &&
        activeSubCat !== "inactivitySettings" && (
          <SubCategoryModal
            subCat={activeSubCat}
            items={data[activeSubCat] || []}
            onAdd={handleAdd}
            onRemove={handleRemove}
            onChange={handleChange}
            onFileUpload={handleFileUpload}
            onSave={handleSave}
            onCancel={closeModal}
          />
        )}

      {/* PIE CHART */}
      {values.length > 0 && <PieChartCard pieData={pieData} />}
    </div>
  );
}
